import React, { useState } from 'react';
import { Outlet, Navigate, NavLink } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const Header = ({ toggleSidebar }) => {
  const { logout, user } = useAuth();

  return (
    <header className="h-16 bg-white border-b border-gray-200 flex items-center justify-between px-4 lg:px-8 z-10 sticky top-0">
      <div className="flex items-center gap-4">
        <button
          onClick={toggleSidebar}
          className="lg:hidden text-brand-dark hover:text-brand-primary p-2 -ml-2"
        >
          <SafeIcon icon={FiIcons.FiMenu} className="text-2xl" />
        </button>
        <div className="hidden md:flex items-center bg-gray-100 rounded-lg px-3 py-1.5 w-64 focus-within:ring-2 focus-within:ring-brand-primary/20">
          <SafeIcon icon={FiIcons.FiSearch} className="text-gray-400 mr-2" />
          <input
            type="text"
            placeholder="Buscar..."
            className="bg-transparent border-none focus:outline-none text-sm w-full text-brand-dark placeholder-gray-400"
          />
        </div>
      </div>

      <div className="flex items-center gap-4">
        {user?.email && (
          <span className="hidden sm:block text-xs text-brand-text truncate max-w-[180px]">
            {user.email}
          </span>
        )}
        <button className="relative p-2 text-gray-500 hover:text-brand-dark transition-colors">
          <SafeIcon icon={FiIcons.FiBell} className="text-xl" />
          <span className="absolute top-1 right-1 w-2 h-2 bg-brand-alert rounded-full"></span>
        </button>
        <div className="h-8 w-px bg-gray-200 mx-2"></div>
        <button
          onClick={logout}
          className="flex items-center gap-2 text-sm text-gray-600 hover:text-brand-alert transition-colors"
        >
          <SafeIcon icon={FiIcons.FiLogOut} />
          <span className="hidden sm:inline">Salir</span>
        </button>
      </div>
    </header>
  );
};

const Sidebar = ({ isOpen, setIsOpen }) => {
  const menuItems = [
    { path: '/dashboard', name: 'Dashboard', icon: FiIcons.FiHome },
    { path: '/trasteros', name: 'Trasteros', icon: FiIcons.FiBox },
    { path: '/clientes', name: 'Clientes', icon: FiIcons.FiUsers },
    { path: '/reservas', name: 'Reservas', icon: FiIcons.FiCalendar },
    { path: '/codigos', name: 'Códigos', icon: FiIcons.FiKey },
    { path: '/pagos', name: 'Pagos', icon: FiIcons.FiDollarSign },
    { path: '/incidencias', name: 'Incidencias', icon: FiIcons.FiAlertCircle },
    { path: '/ajustes', name: 'Ajustes', icon: FiIcons.FiSettings },
  ];

  const sidebarClasses = `fixed inset-y-0 left-0 z-40 w-64 bg-brand-dark text-white transform transition-transform duration-300 ease-in-out lg:translate-x-0 ${
    isOpen ? 'translate-x-0' : '-translate-x-full'
  }`;

  return (
    <>
      {isOpen && (
        <div className="fixed inset-0 z-30 bg-black/50 lg:hidden" onClick={() => setIsOpen(false)} />
      )}

      <aside className={sidebarClasses}>
        <div className="h-16 flex items-center px-6 border-b border-brand-primary">
          <h1 className="text-xl font-bold tracking-tight text-white flex items-center gap-2">
            <span className="w-8 h-8 rounded bg-brand-accent text-brand-dark flex items-center justify-center font-black">B</span>
            Blue Blue On
          </h1>
        </div>

        <nav className="p-4 space-y-1 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 8rem)' }}>
          {menuItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              onClick={() => setIsOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                  isActive
                    ? 'bg-brand-primary text-brand-accent font-medium'
                    : 'text-gray-300 hover:bg-brand-primary/50 hover:text-white'
                }`
              }
            >
              <SafeIcon icon={item.icon} className="text-lg" />
              {item.name}
            </NavLink>
          ))}
          
          {/* Enlace al Manual de Usuario */}
          <a
            href="https://manualblueblueon.doo.ee/"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 px-4 py-3 rounded-lg transition-colors text-gray-300 hover:bg-brand-primary/50 hover:text-white mt-2 border-t border-brand-primary/30 pt-4"
          >
            <SafeIcon icon={FiIcons.FiBook} className="text-lg" />
            Manual de Usuario
          </a>
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-brand-primary bg-brand-dark">
          <div className="flex items-center gap-3 px-4 py-2">
            <div className="w-8 h-8 rounded-full bg-brand-light text-brand-dark flex items-center justify-center font-bold">
              A
            </div>
            <div>
              <p className="text-sm font-medium">Administrador</p>
              <p className="text-xs text-brand-accent">Panel de control</p>
            </div>
          </div>
        </div>
      </aside>
    </>
  );
};

const DashboardLayout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-brand-bg flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 bg-brand-dark rounded-xl flex items-center justify-center">
            <span className="text-brand-accent text-xl font-black">B</span>
          </div>
          <SafeIcon icon={FiIcons.FiLoader} className="animate-spin text-brand-primary text-2xl" />
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return (
    <div className="min-h-screen bg-brand-bg flex">
      <Sidebar isOpen={sidebarOpen} setIsOpen={setSidebarOpen} />
      <div className="flex-1 flex flex-col lg:ml-64 transition-all duration-300">
        <Header toggleSidebar={() => setSidebarOpen(!sidebarOpen)} />
        <main className="flex-1 p-4 lg:p-8 overflow-y-auto">
          <div className="max-w-7xl mx-auto">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;